import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserPlus, LogIn, Mail, Loader2, KeyRound } from "lucide-react";

interface JoinTeamButtonProps {
  challengeId: string;
  onJoined: () => void;
}

const JoinTeamButton: React.FC<JoinTeamButtonProps> = ({
  challengeId,
  onJoined,
}) => {
  const [joining, setJoining] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSent, setLoginSent] = useState(false);
  const [loginMode, setLoginMode] = useState<"magic" | "password">("magic");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSendLoginLink = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!loginEmail) {
      toast.error("Please enter your email");
      return;
    }

    setLoginLoading(true);
    try {
      localStorage.setItem("auth_return_url", window.location.pathname + window.location.search);
      
      const { error } = await supabase.auth.signInWithOtp({
        email: loginEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        if (
          error.message.includes("not found") ||
          error.message.includes("not registered") ||
          error.message.includes("Signups not allowed")
        ) {
          toast.error("No account found. Please complete event registration first.");
          return;
        }
        throw error;
      }

      setLoginSent(true);
      toast.success("Login link sent to your email!");

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (event === "SIGNED_IN" && session?.user) {
            setShowLoginDialog(false);
            setLoginSent(false);
            setLoginEmail("");
            subscription.unsubscribe();
            await joinTeamWithUser(session.user.id);
          }
        }
      );
    } catch (error: any) {
      toast.error(error.message || "Failed to send login link");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoginLoading(true);
    try {
      localStorage.setItem("auth_return_url", window.location.pathname + window.location.search);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in with Google");
      setLoginLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please enter email and password");
      return;
    }

    setLoginLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Invalid email or password");
        } else {
          throw error;
        }
        return;
      }

      if (data.user) {
        setShowLoginDialog(false);
        toast.success("Logged in successfully!");
        await joinTeamWithUser(data.user.id);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in");
    } finally {
      setLoginLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail) {
      toast.error("Please enter your email first");
      return;
    }

    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetSent(true);
      toast.success("Password reset link sent to your email!");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset link");
    } finally {
      setLoginLoading(false);
    }
  };

  const joinTeamWithUser = async (userId: string) => {
    setJoining(true);
    try {
      const { data: existing } = await supabase
        .from("challenge_team_members")
        .select("id")
        .eq("challenge_id", challengeId)
        .eq("user_id", userId)
        .maybeSingle();

      if (existing) {
        toast.info("You're already on this team");
        return;
      }

      const { error } = await supabase
        .from("challenge_team_members")
        .insert({
          challenge_id: challengeId,
          user_id: userId,
          role: "member",
        });

      if (error) throw error;

      toast.success("You've joined the team!");
      onJoined();
    } catch (error: any) {
      console.error("Error joining team:", error);
      toast.error(error.message || "Failed to join team");
    } finally {
      setJoining(false);
    }
  };

  const joinTeam = async () => {
    setJoining(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setJoining(false);
        setShowLoginDialog(true);
        return;
      }

      await joinTeamWithUser(user.id);
    } catch (error: any) {
      console.error("Error joining team:", error);
      toast.error(error.message || "Failed to join team");
      setJoining(false);
    }
  };

  return (
    <>
      <Button
        onClick={joinTeam}
        disabled={joining}
        variant="outline"
        className="gap-2"
      >
        <UserPlus className="h-4 w-4" />
        {joining ? "Joining..." : "Join Team"}
      </Button>

      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-primary" />
              Sign in to join team
            </DialogTitle>
            <DialogDescription>
              Sign in with Google, email link, or password
            </DialogDescription>
          </DialogHeader>

          {showResetPassword ? (
            <div className="space-y-4">
              {!resetSent ? (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="join-reset-email">Email</Label>
                    <Input
                      id="join-reset-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoFocus
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loginLoading}>
                    {loginLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    Don't see the email? Check your spam or junk folder.
                  </p>
                  
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => setShowResetPassword(false)}
                  >
                    Back to login
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm">
                      Reset link sent to <strong>{loginEmail}</strong>
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Click the link in your email to set a new password.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Don't see it? Check your spam or junk folder.
                    </p>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setShowResetPassword(false);
                      setResetSent(false);
                    }}
                  >
                    Back to login
                  </Button>
                </div>
              )}
            </div>
          ) : !loginSent ? (
            <div className="space-y-4">
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={loginLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or
                  </span>
                </div>
              </div>

              <Tabs value={loginMode} onValueChange={(v) => setLoginMode(v as "magic" | "password")} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="magic" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" />
                    Email Link
                  </TabsTrigger>
                  <TabsTrigger value="password" className="text-xs">
                    <KeyRound className="h-3 w-3 mr-1" />
                    Password
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="magic" className="space-y-4 mt-4">
                  <form onSubmit={handleSendLoginLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="join-login-email">Email</Label>
                      <Input
                        id="join-login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={loginLoading}>
                      {loginLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Login Link"
                      )}
                    </Button>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      Don't see the email? Check your spam or junk folder.
                    </p>
                  </form>
                </TabsContent>
                
                <TabsContent value="password" className="space-y-4 mt-4">
                  <form onSubmit={handlePasswordLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="join-email-pass">Email</Label>
                      <Input
                        id="join-email-pass"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="join-password">Password</Label>
                      <Input
                        id="join-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={loginLoading}>
                      {loginLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(true)}
                      className="text-xs text-primary hover:underline w-full text-center"
                    >
                      Forgot password? Set or reset it here
                    </button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-sm">
                  Login link sent to <strong>{loginEmail}</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Click the link in your email to sign in. This window will
                  update automatically.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Don't see it? Check your spam or junk folder.
                </p>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setLoginSent(false);
                  setLoginEmail("");
                }}
              >
                Use a different email
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default JoinTeamButton;
